"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  Scale,
  FileText,
  Shield
} from "lucide-react";

const ServiceCard = ({ title, description, icon: Icon, delay = 0, isReversed = false }) => {
  return (
    <div
      className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:bg-white/15 hover:border-white/30 hover:shadow-xl hover:scale-[1.02] transition-all duration-500 ease-out group w-full"
      style={{
        animationDelay: `${delay}ms`
      }}
      role="article"
      aria-labelledby={`service-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className={`flex items-center gap-8 lg:gap-12 md:gap-8 sm:gap-6 ${isReversed ? 'flex-row-reverse' : 'flex-row'} max-md:flex-col max-md:gap-6`}>
        {/* Icon and Title Side */}
        <div className="flex flex-col items-center space-y-4 flex-shrink-0 max-md:w-full">
          <div className="p-6 bg-gradient-to-br from-white/20 to-white/10 rounded-2xl group-hover:from-white/30 group-hover:to-white/20 transition-all duration-300 shadow-lg">
            <Icon className="w-12 h-12 text-white lg:w-14 lg:h-14 md:w-12 md:h-12 sm:w-10 sm:h-10 group-hover:scale-110 transition-transform duration-300" />
          </div>
          <h3
            id={`service-${title.replace(/\s+/g, '-').toLowerCase()}`}
            className="font-bold text-white font-heading text-center leading-tight text-xl lg:text-2xl md:text-xl sm:text-lg group-hover:text-[#C9C51A] transition-colors duration-300 max-w-[200px]"
          >
            {title}
          </h3>
        </div>

        {/* Description Side */}
        <div className="flex-1 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#D96F2B]/20 to-[#C9C51A]/20 rounded-lg opacity-0  transition-opacity duration-300"></div>
          <p className={`text-white/80 text-base font-body leading-relaxed relative z-10 group-hover:text-white transition-colors duration-300 lg:text-lg md:text-base sm:text-sm ${isReversed ? 'text-right max-md:text-center' : 'text-left max-md:text-center'}`}>
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function ProfessionalSupportServices() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    {
      title: "Legal Process Outsource (LPO)",
      description: "Vasta Legal Process Outsourcing – LPO - refers to the practice of a law firm or corporation obtaining legal support services from our offshore support team around the World. With us and our LPO , we serve many Law firms and large corporations outsourcing in a large document review projects for litigation disclosure or transactional due diligence to an offshore. By using our LPO Services , our Stakeholders concentrate on high-value activities which boosting the firm's productivity and margins.",
      icon: Scale
    },
    {
      title: "Resume Writing – Preparation",
      description: "Vasta help Candidates to Write or make a Professional Resume across all Domains and Industries. We, work Closely with Candidate to make sure their current resume is all sets for the position they are applying. Nevertheless change much at the same time core skills and candidates attributes all respond to the needs of the role, based on the job vacancy. We maintain more than 100% Privacy in all our resume writing Process.",
      icon: FileText
    },
    {
      title: "Background Verification",
      description: "Vasta help its various client/s to hire best from the market and our Background verification is for looking up and compiling criminal records, commercial records and financial records of an individual or an organization. Its typically include past employment verification, credit history, and criminal history and Pre-Employment Screening.",
      icon: Shield
    }
  ];

  return (
    <section className="relative py-0 overflow-hidden" aria-labelledby="professional-support-heading">
      <style >{`
        @keyframes slideInUp {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0px); }
        }
        @keyframes slideInLeft {
          0% { opacity: 0; transform: translateX(-30px); }
          100% { opacity: 1; transform: translateX(0px); }
        }
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(30px); }
          100% { opacity: 1; transform: translateX(0px); }
        }
        @keyframes diamondFade {
          0%, 100% { opacity: 0.4; transform: rotate(45deg) scale(1); }
          50% { opacity: 0.9; transform: rotate(45deg) scale(1.2); }
        }
        @keyframes diamondPulse {
          0%, 100% { opacity: 0.6; transform: rotate(45deg) scale(0.8); }
          25% { opacity: 0.8; transform: rotate(45deg) scale(1.1); }
          50% { opacity: 1; transform: rotate(45deg) scale(1.3); }
          75% { opacity: 0.8; transform: rotate(45deg) scale(1.1); }
        }
        .diamond {
          width: 6px;
          height: 6px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(201, 197, 26, 0.7));
          transform: rotate(45deg);
          animation: diamondFade 4s ease-in-out infinite;
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.4), 0 0 16px rgba(201, 197, 26, 0.3);
          border-radius: 1px;
        }
        .diamond-pattern-tl {
          position: absolute;
          top: 2rem;
          left: 2rem;
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 12px;
          z-index: 10;
        }
        .diamond-pattern-br {
          position: absolute;
          bottom: 2rem;
          right: 2rem;
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 12px;
          z-index: 10;
        }
        .diamond-pattern-tl .diamond:nth-child(n+1):nth-child(-n+8) { animation-delay: 0s; opacity: 0.9; animation-name: diamondPulse; }
        .diamond-pattern-tl .diamond:nth-child(n+9):nth-child(-n+16) { animation-delay: 0.3s; opacity: 0.8; }
        .diamond-pattern-tl .diamond:nth-child(n+17):nth-child(-n+24) { animation-delay: 0.6s; opacity: 0.7; }
        .diamond-pattern-tl .diamond:nth-child(n+25):nth-child(-n+32) { animation-delay: 0.9s; opacity: 0.6; }
        .diamond-pattern-tl .diamond:nth-child(n+33):nth-child(-n+40) { animation-delay: 1.2s; opacity: 0.5; }
        .diamond-pattern-tl .diamond:nth-child(n+41):nth-child(-n+48) { animation-delay: 1.5s; opacity: 0.4; }
        .diamond-pattern-tl .diamond:nth-child(n+49):nth-child(-n+56) { animation-delay: 1.8s; opacity: 0.3; }
        .diamond-pattern-tl .diamond:nth-child(n+57):nth-child(-n+64) { animation-delay: 2.1s; opacity: 0.2; }

        .diamond-pattern-br .diamond:nth-child(n+1):nth-child(-n+8) { animation-delay: 2.1s; opacity: 0.2; animation-name: diamondPulse; }
        .diamond-pattern-br .diamond:nth-child(n+9):nth-child(-n+16) { animation-delay: 1.8s; opacity: 0.3; }
        .diamond-pattern-br .diamond:nth-child(n+17):nth-child(-n+24) { animation-delay: 1.5s; opacity: 0.4; }
        .diamond-pattern-br .diamond:nth-child(n+25):nth-child(-n+32) { animation-delay: 1.2s; opacity: 0.5; }
        .diamond-pattern-br .diamond:nth-child(n+33):nth-child(-n+40) { animation-delay: 0.9s; opacity: 0.6; }
        .diamond-pattern-br .diamond:nth-child(n+41):nth-child(-n+48) { animation-delay: 0.6s; opacity: 0.7; }
        .diamond-pattern-br .diamond:nth-child(n+49):nth-child(-n+56) { animation-delay: 0.3s; opacity: 0.8; }
        .diamond-pattern-br .diamond:nth-child(n+57):nth-child(-n+64) { animation-delay: 0s; opacity: 0.9; }

        .card-enter-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }

        .card-enter-right {
          animation: slideInRight 0.8s ease-out forwards;
        }

        .card-enter-up {
          animation: slideInUp 0.8s ease-out forwards;
        }

        /* Mobile responsive diamonds */
        @media (max-width: 768px) {
          .diamond-pattern-tl {
            top: 1rem;
            left: 1rem;
            grid-template-columns: repeat(6, 1fr);
            gap: 8px;
          }
          .diamond-pattern-br {
            bottom: 1rem;
            right: 1rem;
            grid-template-columns: repeat(6, 1fr);
            gap: 8px;
          }
          .diamond {
            width: 4px;
            height: 4px;
          }
        }

        @media (max-width: 480px) {
          .diamond-pattern-tl {
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
          }
          .diamond-pattern-br {
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
          }
          .diamond {
            width: 3px;
            height: 3px;
          }
        }
      `}</style>

      {/* Main Container Box */}
      <div className="mx-2 lg:mx-6 xl:mx-8 md:mx-4 sm:mx-2 bg-gradient-to-br from-[#1719A8] to-[#0F1280] rounded-3xl border border-white/20 backdrop-blur-sm relative">
        <div className="relative overflow-hidden">
          {/* Background Gradient */}
          <div className="absolute inset-0" aria-hidden="true" />

          {/* Accent Glow Effects */}
          <div
            className="absolute top-10 left-1/2 transform -translate-x-1/2 w-96 h-32 rounded-full blur-3xl opacity-20 lg:opacity-20 md:opacity-15 sm:opacity-10 lg:w-96 lg:h-32 md:w-72 md:h-24 sm:w-48 sm:h-16"
            style={{
              backgroundColor: '#D96F2B',
            }}
            aria-hidden="true"
          />

          <div
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-80 h-40 rounded-full blur-3xl opacity-15 lg:w-80 lg:h-40 md:w-60 md:h-30 sm:w-40 sm:h-20"
            style={{
              backgroundColor: '#C9C51A',
            }}
            aria-hidden="true"
          />

          {/* Decorative Diamonds - Top Left */}
          <div className="diamond-pattern-tl" aria-hidden="true">
            {Array.from({ length: 64 }).map((_, i) => (
              <div key={`tl-${i}`} className="diamond" />
            ))}
          </div>

          {/* Decorative Diamonds - Bottom Right */}
          <div className="diamond-pattern-br" aria-hidden="true">
            {Array.from({ length: 64 }).map((_, i) => (
              <div key={`br-${i}`} className="diamond" />
            ))}
          </div>

          {/* Content Container */}
          <div className="container mx-auto px-6 py-16 sm:py-12 md:py-14 lg:py-16 relative z-20">
            <div className={`
              transition-all duration-1000 ease-out
              ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
            `}>

              {/* Header Section */}
              <div className="text-center mb-12 lg:mb-16 md:mb-12 sm:mb-8">
                <div className="inline-flex items-center px-8 py-3 bg-white/90 backdrop-blur-md border border-white/60 rounded-full mb-4 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <span className="w-3 h-3 bg-[#1719A8] rounded-full mr-4 animate-pulse shadow-lg"></span>
                  <span style={{ color: '#1719A8' }} className="text-sm md:text-base font-bold uppercase tracking-wider">
                    Professional Support Services
                  </span>
                </div>
                <header>
                  <h1 id="professional-support-heading" className="text-5xl xl:text-7xl lg:text-6xl md:text-5xl sm:text-3xl font-bold text-white leading-tight font-heading mb-8 lg:mb-8 md:mb-6 sm:mb-4">
                    Professional Support <br/>
                    <span className="bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] bg-clip-text text-transparent">
                      Services
                    </span>
                  </h1>
                  <p className="text-lg lg:text-xl md:text-lg sm:text-base text-white/90 leading-relaxed font-body max-w-4xl mx-auto lg:max-w-4xl md:max-w-3xl sm:max-w-xl">
                    Comprehensive professional support services including legal process outsourcing, resume writing, and background verification to enhance your business operations and hiring processes.
                  </p>
                </header>
              </div>

              {/* Vertical Stack of Service Cards with Zig-Zag Layout */}
              <div className="space-y-8 lg:space-y-12 md:space-y-10 sm:space-y-8 max-w-6xl mx-auto" role="region" aria-label="Professional Support Services">
                {services.map((service, index) => (
                  <div
                    key={`service-${index}`}
                    className={`
                      ${isVisible ? 'opacity-100' : 'opacity-0'}
                      ${index % 2 === 0 ? 'card-enter-left' : 'card-enter-right'}
                      max-md:card-enter-up
                    `}
                    style={{
                      animationDelay: `${(index + 1) * 300}ms`
                    }}
                  >
                    <ServiceCard
                      title={service.title}
                      description={service.description}
                      icon={service.icon}
                      delay={(index + 1) * 300}
                      isReversed={index % 2 !== 0}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
